import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardActionArea, Grid, Typography } from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";
import SettingsIcon from "@mui/icons-material/Settings";

import { PERMISSIONS } from "../../constants";
import { isAdmin } from "../../functions";

// Entry point for the radicados module, mirroring PqrsdHub: it lists the lists
// a user can reach (all radicados, their own communications, the OCR config).
// A user with a single option has nothing to choose, so they are redirected
// straight to it and never see this menu.
export default function FiledHub() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);

  const perms = useMemo(() => user?.permissions || [], [user]);
  const canList = perms.includes(PERMISSIONS.FILED.LIST);
  const canListRequests = perms.includes(PERMISSIONS.REQUESTSFILED.LIST);
  // Config is ADMIN-only, and the check is on the role rather than
  // FILED.CONFIG: permissions are a snapshot stored per user, so a permission
  // added to ROLES.ADMIN today is missing from every existing admin's array.
  const canConfig = isAdmin(user);

  const options = useMemo(() => {
    const items = [];
    const iconSx = { fontSize: { xs: 44, md: 56 } };
    if (canList) {
      items.push({
        key: "list",
        label: "Radicados",
        description: "Consulta y busca los documentos radicados.",
        icon: <DescriptionIcon sx={iconSx} />,
        path: "/filedlist",
      });
    }
    if (canListRequests) {
      items.push({
        key: "requests",
        label: "Mis comunicaciones",
        description: "Los radicados en los que participas como remitente o destinatario.",
        icon: <MarkunreadMailboxIcon sx={iconSx} />,
        path: "/filedlist/requests",
      });
    }
    if (canConfig) {
      items.push({
        key: "config",
        label: "Configuración",
        description: "Extracción de texto (OCR), anulación y notificación de radicados.",
        icon: <SettingsIcon sx={iconSx} />,
        path: "/filedlist/config",
      });
    }
    return items;
  }, [canList, canListRequests, canConfig]);

  // With a single option there is nothing to choose: skip the menu and land on
  // the module itself. From two options up the menu must render, otherwise a
  // non-admin holding both lists (ARCHIVER, PQRSD_SUPERVISOR) would be pushed
  // straight to /filedlist and lose every entry point to "Mis comunicaciones".
  const passthroughPath = options.length === 1 ? options[0].path : null;
  const skipMenu = !!passthroughPath;

  useEffect(() => {
    if (skipMenu) navigate(passthroughPath, { replace: true });
  }, [skipMenu, passthroughPath, navigate]);

  if (options.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Sin acceso a radicados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tu usuario no tiene permisos para ningún módulo de radicados.
        </Typography>
      </Box>
    );
  }

  // Render nothing while the redirect runs, so the menu never flashes.
  if (skipMenu) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Radicados
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Selecciona un módulo
      </Typography>

      <Grid
        container
        spacing={3}
        justifyContent="center"
        sx={{ width: "100%", maxWidth: 900 }}
      >
        {options.map((opt) => (
          <Grid item xs={12} sm={6} md={4} key={opt.key}>
            <Card
              sx={{
                height: "100%",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardActionArea onClick={() => navigate(opt.path)} sx={{ height: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                  }}
                >
                  <Box sx={{ color: "primary.main", mb: 1.5 }}>{opt.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {opt.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {opt.description}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
